import React, { useEffect, useState } from "react";

/**
 * DataBand
 * ---------
 * Institutional replacement for the legacy scrolling market ticker.
 *
 * Fetches /api/ticker and renders a static, monospaced strip of the four
 * data points that actually matter for the kind of client Vexent serves:
 *   • S&P 500       (equity risk)
 *   • US 10-yr yield (risk-free rate)
 *   • Gold / oz     (real-asset benchmark)
 *   • WTI / bbl     (energy)
 *
 * Muted palette, thin type, no scroll animation. Sits under Header,
 * hidden on very narrow phones (<500px) where it would truncate.
 */
const DataBand = () => {
  const [ticks, setTicks] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/ticker`;
        const res = await fetch(url, { credentials: "omit" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTicks(data);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60 * 1000);  // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const items = pickItems(ticks);
  const asOf = ticks && (ticks.asOf || ticks.timestamp);

  return (
    <div className="hidden sm:block border-b border-[#0B1F33]/10 bg-[#0B1F33] text-white/90" data-testid="data-band">
      <div className="max-w-[1400px] mx-auto px-6 py-2.5 flex items-center gap-6 md:gap-10 text-[11px] font-mono-tick">
        <span className="uppercase tracking-[0.28em] font-condensed text-[#B8E9F1] font-semibold">
          Vexent · Data Band
        </span>
        <div className="flex-1 flex items-center gap-6 md:gap-10 overflow-x-auto no-scrollbar">
          {items.map((t) => (
            <Tick key={t.label} label={t.label} value={t.value} change={t.change} />
          ))}
        </div>
        {asOf && (
          <span className="hidden md:block text-[10px] uppercase tracking-[0.22em] text-white/40 font-condensed">
            {formatAsOf(asOf)}
          </span>
        )}
      </div>
    </div>
  );
};

const Tick = ({ label, value, change }) => {
  const positive = typeof change === "number" ? change >= 0 : String(change || "").startsWith("+");
  return (
    <div className="flex items-baseline gap-2 whitespace-nowrap">
      <span className="uppercase tracking-[0.2em] text-white/50 font-condensed font-semibold">{label}</span>
      <span className="tabular-nums text-white">{value}</span>
      {change !== undefined && change !== null && (
        <span className={`tabular-nums text-[10px] ${positive ? "text-[#7ce0a1]" : "text-[#ff8a8a]"}`}>
          {typeof change === "number" ? (positive ? "+" : "") + change.toFixed(2) : change}
        </span>
      )}
    </div>
  );
};

const WANT = new Set(["SPX", "S&P 500", "S&P500", "US10Y", "US 10Y", "10Y", "GOLD", "XAU", "WTI", "CRUDE"]);

function pickItems(ticks) {
  if (!ticks) return DEFAULTS;
  const list = Array.isArray(ticks) ? ticks : (ticks.items || ticks.ticks || []);
  const found = [];
  for (const it of list) {
    const label = (it.label || it.symbol || it.name || "").toUpperCase();
    if (WANT.has(label) || WANT.has(label.replace(/\s+/g, ""))) {
      found.push({
        label: normalize(label),
        value: it.value ?? it.price ?? it.last ?? "",
        change: it.change ?? it.pctChange ?? it.delta ?? null,
      });
    }
  }
  return found.length ? found : DEFAULTS;
}

function normalize(l) {
  if (l.startsWith("S&P")) return "S&P 500";
  if (l.startsWith("US") || l.includes("10Y")) return "US 10Y";
  if (l === "XAU" || l === "GOLD") return "GOLD";
  if (l === "WTI" || l === "CRUDE") return "WTI";
  return l;
}

const DEFAULTS = [
  { label: "S&P 500", value: "—" },
  { label: "US 10Y", value: "—" },
  { label: "GOLD",   value: "—" },
  { label: "WTI",    value: "—" },
];

function formatAsOf(t) {
  try {
    const d = new Date(t);
    return `As of ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })} UTC`;
  } catch {
    return "";
  }
}

export default DataBand;
